import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  getTenantAlias, 
  initializeTenant, 
  getTenantFromDomain, 
  setTenantAlias as storeTenantAlias,
  clearTenantData,
  getTenantInfo
} from '../utils/tenant';
import { setGlobalTenantAlias } from '../services/api.service';

// Create the context
const TenantContext = createContext(null);

// Provider component
export function TenantProvider({ children }) {
  const [tenantAlias, setTenantAlias] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize tenant alias on component mount
  useEffect(() => {
    async function initTenant() {
      try {
        setIsLoading(true);
        setError(null); // Clear any previous errors
        
        // Check URL for refresh parameter
        const urlParams = new URLSearchParams(window.location.search);
        const forceRefresh = urlParams.get('refresh') !== null;
        
        if (forceRefresh) {
          console.log('Force refresh requested, resolving from domain');
          const domainTenant = await getTenantFromDomain();
          if (domainTenant) {
            console.log(`Resolved tenant from domain: ${domainTenant}`);
            storeTenantAlias(domainTenant);
            setTenantAlias(domainTenant);
          } else {
            console.error('Domain resolution failed');
            setError('Invalid URL. Could not determine tenant from domain name. Please check your URL or contact support.');
            return;
          }
        } else {
          console.log('Calling getTenantAlias()...');
          const alias = await getTenantAlias();
          
          if (alias) {
            console.log(`Tenant alias resolved: ${alias}`);
            setTenantAlias(alias);
          } else {
            console.log('No tenant in storage, trying domain resolution');
            const domainTenant = await getTenantFromDomain();
            
            if (domainTenant) {
              console.log(`Resolved tenant from domain: ${domainTenant}`);
              storeTenantAlias(domainTenant);
              setTenantAlias(domainTenant);
            } else {
              console.error('Domain resolution failed');
              setError('Invalid URL. Could not determine tenant from domain name. Please check your URL or contact support.');
              return;
            }
          }
        }
        
        // Get full tenant information
        const hostname = window.location.hostname;
        const tenantData = await getTenantInfo(hostname);
        if (tenantData) {
          console.log('Tenant information loaded:', tenantData);
          setTenantInfo(tenantData);
        }
        
        console.log(`TenantContext initialized with tenant alias: ${tenantAlias}`);
      } catch (err) {
        console.error('Failed to initialize tenant:', err);
        setError(err.message || 'Failed to initialize tenant. Please try refreshing the page or contact support.');
      } finally {
        setIsLoading(false);
      }
    }

    initTenant();
  }, []);

  // Update global tenant alias for API calls when tenantAlias changes
  useEffect(() => {
    if (tenantAlias) {
      console.log(`Setting tenant alias in context: ${tenantAlias}`);
      setGlobalTenantAlias(tenantAlias);
      // Also store in localStorage for persistence
      storeTenantAlias(tenantAlias);
    }
  }, [tenantAlias]);

  // Function to update tenant alias
  const updateTenantAlias = async (newAlias) => {
    try {
      setIsLoading(true);
      
      if (newAlias) {
        storeTenantAlias(newAlias);
        setTenantAlias(newAlias);
        console.log(`TenantContext updated with tenant alias: ${newAlias}`);
        return newAlias;
      } else {
        // If no alias provided, try to resolve from domain
        const domainTenant = await getTenantFromDomain();
        if (domainTenant) {
          storeTenantAlias(domainTenant);
          setTenantAlias(domainTenant);
          console.log(`TenantContext updated with tenant alias from domain: ${domainTenant}`);
          return domainTenant;
        }
        
        // Domain resolution failed
        setError('Invalid URL. Could not determine tenant from domain name.');
        return null;
      }
    } catch (err) {
      console.error('Failed to update tenant:', err);
      setError(err.message || 'Failed to update tenant');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to refresh tenant (clear and resolve again)
  const refreshTenantAlias = async () => {
    try {
      setIsLoading(true);
      clearTenantData();
      
      // Try to resolve from domain first
      const domainTenant = await getTenantFromDomain();
      if (domainTenant) {
        storeTenantAlias(domainTenant);
        setTenantAlias(domainTenant);
        console.log(`TenantContext refreshed with tenant alias from domain: ${domainTenant}`);
        return domainTenant;
      }
      
      // Domain resolution failed
      setError('Invalid URL. Could not determine tenant from domain name.');
      return null;
    } catch (err) {
      console.error('Failed to refresh tenant:', err);
      setError(err.message || 'Failed to refresh tenant');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Context value
  const value = {
    tenantAlias,
    tenantInfo,
    isLoading,
    error,
    updateTenantAlias,
    refreshTenantAlias
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// Custom hook to use the tenant context
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === null) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

// Export the context itself if needed
export default TenantContext; 